import { useEffect, useState } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import type {
  CreditNoteDto,
  InvoiceDto,
  ReceiptDto,
  VoucherWithInvoiceDto,
} from '@gty/common';

import { apiUrl } from '../api/config';
import { CreditNoteDocument } from '../credit-notes/CreditNoteDocument';
import { InvoiceSheet } from '../invoices/InvoiceDocumentPage';
import { ReceiptDocument } from '../payments/ReceiptDocument';
import { VoucherDocument } from '../vouchers/VoucherDocument';

/**
 * The page headless Chromium photographs.
 *
 * Deliberately OUTSIDE the app shell and outside `RequireAuth`: there is no
 * session in the print browser, only a 60-second token bound to this one
 * document. It fetches through `/print-data`, which validates that token
 * itself — see `PrintController` for why that is narrower than teaching the
 * main guard about print tokens.
 *
 * It renders the SAME document components the View screens use, which is the
 * whole point of approach A — there is no second template to drift.
 */

type Loaded =
  | { type: 'invoice'; data: InvoiceDto }
  | { type: 'voucher'; data: VoucherWithInvoiceDto }
  | { type: 'credit-note'; data: CreditNoteDto }
  /**
   * A receipt is a PROJECTION — the server derives it from the payment row and
   * the ledger around it rather than storing one. From here that is invisible
   * and deliberately so: the print pipeline only ever needed one id to resolve
   * to one document.
   */
  | { type: 'receipt'; data: ReceiptDto };

export default function PrintPage(): JSX.Element {
  const { type, id } = useParams<{ type: string; id: string }>();
  const [params] = useSearchParams();
  const token = params.get('token') ?? '';

  const [loaded, setLoaded] = useState<Loaded | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;

    void (async () => {
      try {
        const response = await fetch(
          apiUrl(`/print-data/${type ?? ''}/${encodeURIComponent(id ?? '')}?token=${encodeURIComponent(token)}`),
        );
        if (!response.ok) {
          throw new Error(`Print data returned ${response.status}`);
        }
        const data = (await response.json()) as never;
        if (cancelled) return;
        setLoaded({ type, data } as Loaded);
      } catch (err: unknown) {
        if (!cancelled) setError(err instanceof Error ? err.message : 'Could not load document');
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [type, id, token]);

  if (error !== null) {
    // Rendered rather than thrown so Puppeteer's `waitForSelector` times out
    // with a page that says what went wrong instead of a blank white PDF.
    return (
      <div data-print-error style={{ padding: 40, fontFamily: 'sans-serif', color: '#8B0000' }}>
        {error}
      </div>
    );
  }

  if (loaded === null) return <div />;

  return (
    /**
     * `data-print-ready` is the signal `PdfService` waits on.
     *
     * Network-idle alone is not enough — it fires when requests stop, which can
     * precede React committing the markup. This attribute appears only once the
     * document itself is in the tree, so the snapshot can never catch a
     * half-rendered page.
     */
    <div data-print-ready="true" style={{ background: '#fff' }}>
      {/*
        Strip everything that adds height around a sheet.
        The page box is exactly one sheet tall, so the browser's default 8px
        body margin — or the gap the multi-sheet documents put between pages —
        is enough to push each sheet onto a second, blank page.
      */}
      <style>{`
        html, body { margin: 0; padding: 0; background: #fff; }
        [data-print-sheet] { box-shadow: none !important; }

        /* The document wrappers centre and space their sheets for on-screen
           preview; in print each sheet must butt directly against the next. */
        [data-print-ready] > div { gap: 0 !important; display: block !important; }

        /*
          ONE SHEET = ONE PAGE, ENFORCED.

          This is what killed the blank pages 2 and 4. Relying on the sheet's
          height matching the page box exactly is hopeless: a sub-pixel
          difference, a flex gap, or a rounding error spills a few tenths of a
          pixel onto the next page and Chromium dutifully emits a blank one.

          Forcing the break after each sheet — and suppressing it on the last,
          which would otherwise add a trailing blank — makes pagination
          deterministic regardless of fractional heights.
        */
        [data-print-sheet] { break-after: page; page-break-after: always; }
        [data-print-sheet]:last-child { break-after: auto; page-break-after: auto; }
        /* Never split a sheet across pages either. */
        [data-print-sheet] { break-inside: avoid; page-break-inside: avoid; }
      `}</style>
      {loaded.type === 'invoice' && <InvoiceSheet invoice={loaded.data} />}
      {loaded.type === 'voucher' && <VoucherDocument v={loaded.data} />}
      {loaded.type === 'credit-note' && <CreditNoteDocument note={loaded.data} />}
      {loaded.type === 'receipt' && <ReceiptDocument receipt={loaded.data} />}
    </div>
  );
}
